import React from 'react';
import { FieldRenderProps, FieldWrapper } from '@progress/kendo-react-form';
import styled from 'styled-components';
import { enums, getHtmlFromMarkdown } from '../../../utils';
import {
  Checkbox,
  KendoError,
  Input,
  MaskedTextBox,
  Switch,
  TextArea,
  MultiSelect,
} from '../simple-styled-components';
import DatePicker from '../form-field/date-picker';
import DurationPicker from '../form-field/duration-picker';
import Signer from '../form-field/signer';
import WeeklySchedule from '../form-field/weekly-schedule';
import ReducedSchedule from '../form-field/reduced-schedule';
import Upload from '../form-field/upload';
import LeavePlanGraph from '../form-field/leave-plan-graph';
import LoadingIndicator from '../form-field/loading-indicator';
import RadioButtonGroup from '../form-field/radio-button-group';
import TimePicker from '../form-field/time-picker';
import ClaimsDropDownList from '../../base/claims-drop-down-list';
import DropDownList from '../form-field/dropdown-list';
import VariableSchedule from '../form-field/variable-schedule';
import { Label as KendoLabel } from '@progress/kendo-react-labels';
import Label from '../form-field/label';
import { FormContext, IFormContext } from '../../../contexts/form-context';
import { rem } from '../../../style';
import { Elements } from '@kontent-ai/delivery-sdk';
import * as options from '../options';
import { useAutosaveGetFieldValue } from '../../../hooks/use-data';
import { processNavigationObject } from '../form/utils';
import { useLocation } from 'react-router-dom';
import { pushAnalyticsDataLayer } from '../../../utils/analytics/helpers';
import RadioWithDetails from '../form-field/radio-with-details';
import { RotationalSchedule } from '../form-field/weekly-schedule/RotationalSchedule';
import { ReducedScheduleHours } from '../form-field/weekly-schedule/ReducedScheduleHours';

import {
  toHTMLElement,
  isTextEntryElement,
} from '../../../utils/analytics/kendo-helpers';

export const componentMap: {
  [key: string]: any;
} = {
  Checkbox,
  DatePicker,
  DropDownList,
  Input,
  LeavePlanGraph,
  MaskedTextBox,
  RadioButtonGroup,
  ClaimsDropDownList,
  Signer,
  Switch,
  TextArea,
  TimePicker,
  WeeklySchedule,
  Upload,
  MultiSelect,
  LoadingIndicator,
  DurationPicker,
  ReducedSchedule,
  RadioWithDetails,
  RotationalSchedule,
  ReducedScheduleHours,
  VariableSchedule,
};

export const simpleComponentList = [
  'Checkbox',
  'Error',
  'Input',
  'MaskedTextBox',
  'Switch',
  'TextArea',
  'TimePicker',
];

export interface IFormFieldWrapperProps extends FieldRenderProps {
  fieldOptionsTemplate: options.IFieldOptions;
  currentStepIndex: number;
  moveToStep?: Function;
}

export interface IHandleFormFieldValueChangeParams {
  fieldOptionsTemplate: options.IFieldOptions;
  formContext: { [name: string]: any };
  formOnChange: (
    name: string,
    options: {
      value: any;
    },
  ) => void;
  formValueGetter: (name: string) => any;
  lastKnownValue: any;
  setLastKnownValue: React.Dispatch<any>;
  lastKnownName: string;
  setLastKnownName: React.Dispatch<string>;
  touched: boolean;
  value: any;
  name: string;
}

export const handleFormFieldValueChange = ({
  fieldOptionsTemplate,
  formContext,
  formOnChange,
  formValueGetter,
  lastKnownValue,
  setLastKnownValue,
  touched,
  value,
  lastKnownName,
  setLastKnownName,
  name,
}: IHandleFormFieldValueChangeParams) => {
  if (
    touched &&
    !!value &&
    value !== lastKnownValue &&
    name === lastKnownName
  ) {
    let shouldUpdateLastKnown = false;
    if (fieldOptionsTemplate.updateFieldsOnChange) {
      // update field on change template
      const ufoct = fieldOptionsTemplate.updateFieldsOnChange;

      Object.keys(ufoct).forEach((k: string) => {
        // option value template
        const ovt = ufoct[k];
        let ov = ovt;

        // only process ovt if it's a string beginning with an option replacement directive ($)
        if (ovt !== null && typeof ovt === 'string' && ovt.startsWith('$')) {
          ov = options.getOptionValue(ovt, formValueGetter, formContext);
        }

        formOnChange(k, { value: undefined });
        setTimeout(() => formOnChange(k, { value: ov }));
      });

      shouldUpdateLastKnown = true;
    }

    if (fieldOptionsTemplate.updateFieldsOnValue) {
      const ufovArray = fieldOptionsTemplate.updateFieldsOnValue;

      ufovArray.forEach((entry) => {
        const matches = Array.isArray(entry.thisValueMatches)
          ? entry.thisValueMatches.includes(value)
          : entry.thisValueMatches === value;

        if (matches) {
          Object.keys(entry.setFields).forEach((k: string) => {
            // option value template
            const ovt = entry.setFields[k];
            let ov = ovt;

            // only process ovt if it's a string beginning with an option replacement directive ($)
            if (
              ovt !== null &&
              typeof ovt === 'string' &&
              ovt.startsWith('$')
            ) {
              ov = options.getOptionValue(ovt, formValueGetter, formContext);
            }

            formOnChange(k, { value: undefined });
            setTimeout(() => formOnChange(k, { value: ov }));
          });
        }
      });

      shouldUpdateLastKnown = true;
    }
    if (shouldUpdateLastKnown) {
      setLastKnownValue(value);
      setLastKnownName(name);
    }
  }
};

const getFlagMap = (flags: Elements.MultipleChoiceElement) => {
  return {
    card:
      flags?.value.filter((f) => f.codename === enums.FormFlags.card).length >
      0,
    plainLabel:
      flags?.value.filter((f) => f.codename === enums.FormFlags.plainLabel)
        .length > 0,
    required:
      flags?.value.filter((f) => f.codename === enums.FormFlags.required)
        .length > 0,
  };
};

const FieldCard = styled.div`
  background-color: white;
  border: 0;
  border-radius: 8px;
  box-shadow: 0 -3px 12px #e0e7ed;
  padding: ${rem(20)};
`;

const handleFormFieldKeyDown = (e: React.KeyboardEvent<any>) => {
  if (e.code === 'Enter' && (e.target as HTMLElement).nodeName !== 'TEXTAREA') {
    e.preventDefault();
  }
};

export const FormFieldWrapper = (p: IFormFieldWrapperProps) => {
  const {
    id,
    validationMessage,
    visited,
    formFieldType,
    formFieldLabel,
    formFieldLabelDetails,
    fieldFlags,
    fieldOptionsTemplate,
    required,
    touched,
    value,
    processedProps,
    formOptionsTemplate,
    formValueGetter,
    formOnChange,
    reprocessProps,
    currentStepIndex,
    moveToStep,
    onChange: kendoOnChange,
    onBlur: kendoOnBlur,
    name,
    sectionname,
    ...others
  } = p;

  const FieldComponent = componentMap[formFieldType];
  const flags = getFlagMap(fieldFlags);
  const [lastKnownName, setLastKnownName] = React.useState(name);
  const [lastKnownValue, setLastKnownValue] = React.useState(value);
  const [hasBeenReset, setHasBeenReset] = React.useState(false);
  const { formContext, setFormContext } = React.useContext(FormContext);
  const location = useLocation();
  const { lastPath: formName } = processNavigationObject({
    location,
    history: undefined as any,
    match: undefined as any,
  });
  const { data: autoSaveFieldValue } = useAutosaveGetFieldValue(formName, name);
  const [isAutoSaveValueUsed, setIsAutoSaveValueUsed] = React.useState(false);
  const hasBeenTouched = React.useRef(false);

  const onBlurHandler = React.useRef<() => void>(() => {});

  const hasPrefillDefaultedValue = Object.hasOwn(
    formContext.defaults ?? {},
    name,
  );
  const prefillActive = Object.keys(formContext.defaults ?? {}).length > 0;
  // A prefilled field may adopt the (prefill-seeded) autosave value, but a
  // falsy prefill default means the field is intentionally blank, so leave it.
  const canHydrateFromAutosave = hasPrefillDefaultedValue
    ? !!formContext.defaults?.[name]
    : !prefillActive;

  onBlurHandler.current = () => {
    hasBeenTouched.current = true;
    handleFormFieldValueChange({
      fieldOptionsTemplate,
      formContext,
      formOnChange,
      formValueGetter,
      lastKnownValue,
      setLastKnownValue,
      lastKnownName,
      setLastKnownName,
      touched: hasBeenTouched.current,
      value: formValueGetter(name),
      name,
    });
    kendoOnBlur();
  };

  const onChange =
    (propsDataGtmDl: any) =>
    (e: { target?: any; value?: any; reset?: boolean }) => {
      if (
        !isAutoSaveValueUsed &&
        canHydrateFromAutosave &&
        !!autoSaveFieldValue &&
        touched &&
        (e?.target?.value === null || e?.target?.value === undefined) &&
        (e?.value === null || e?.value === undefined) &&
        e?.reset !== true
      ) {
        setIsAutoSaveValueUsed(true);
        kendoOnChange({ target: { value: autoSaveFieldValue } });
        return;
      }

      if (e?.reset) {
        setHasBeenReset(true);
        kendoOnChange({ target: { value: undefined } });
        return;
      }

      setIsAutoSaveValueUsed(false);

      if (
        currentStepIndex < (formContext.stepIndexWithLeavePlanGraph || -1) &&
        formContext.currentLeavePlan
      ) {
        setFormContext((c: IFormContext) => {
          const nc = { ...c };
          delete nc.currentLeavePlan;
          return nc;
        });
      }

      // propagate
      kendoOnChange(e);

      // analytics ////////////////////////////////////////////////
      const active = document.activeElement as HTMLElement | null;
      const btn = active?.closest('button');
      const hostEl: HTMLElement | null =
        toHTMLElement(e?.target?.element) || toHTMLElement(e) || null;

      // Only update attribute4 when the interaction came from a button
      // this prevents dropdown and text values being sent to analytics
      if (btn?.tagName === 'BUTTON') {
        propsDataGtmDl.attribute4 = e?.value ?? e?.target?.value ?? null;
        propsDataGtmDl.category = 'button';
      }

      if (!isTextEntryElement(hostEl)) {
        pushAnalyticsDataLayer(hostEl, propsDataGtmDl, id);
      }
    };

  // autosave: use this if updating from kendoOnChange to autosave
  const onBlur = React.useCallback(() => onBlurHandler.current(), []);

  // Reprocess this field's props whenever its committed value changes.
  // Kendo v6's `valueGetter` is a stable reference, so `reprocessProps`
  // (whose deps rely on that reference) no longer re-runs on value changes.
  // Tracking `value` here re-resolves any `$`-directives in the props template
  // that read other fields — e.g. the scheduled-callback dropdown's
  // `$availableCallCenterTimesBasedOn.scheduledDate` data source. When
  // `scheduledDate` changes it resets this field (`scheduledTime`) to null,
  // and that value change reprocesses the props so the resolved date (and the
  // resulting query) reflect the newly selected day.
  React.useEffect(() => {
    reprocessProps();
  }, [reprocessProps, value]);

  React.useEffect(() => {
    if (
      !touched &&
      !value &&
      canHydrateFromAutosave &&
      !isAutoSaveValueUsed &&
      !hasBeenReset &&
      autoSaveFieldValue !== undefined &&
      autoSaveFieldValue !== null
    ) {
      setIsAutoSaveValueUsed(true);
      kendoOnChange({ target: { value: autoSaveFieldValue } });
    }
  }, [
    name,
    isAutoSaveValueUsed,
    value,
    kendoOnChange,
    autoSaveFieldValue,
    touched,
    hasBeenReset,
  ]);

  // only WeeklySchedule needs this prop at the moment
  // only LeavePlanGraph needs the form to check the optionsTemplate
  if (
    formFieldType === enums.FormFieldTypes.weeklyschedule ||
    formFieldType === enums.FormFieldTypes.claimsdropdownlist ||
    formFieldType === enums.FormFieldTypes.reducedschedule ||
    formFieldType === enums.FormFieldTypes.reducedschedulehours
  ) {
    others.processedProps = processedProps;
  } else if (formFieldType === enums.FormFieldTypes.leaveplangraph) {
    others.formOptionsTemplate = formOptionsTemplate;
    others.moveToStep = moveToStep;
  }

  // include formValueGetter on custom fields
  if (!simpleComponentList.includes(formFieldType)) {
    others.formValueGetter = formValueGetter;
  }

  return (
    <FieldWrapper className="tw-mb-6 tw-mt-0 tw-mx-0 sm:max-[440px]:tw-ml-0 sm:max-[989px]:tw-ml-[-12px]">
      <div className="tw-flex tw-items-center tw-gap-1">
        {flags.plainLabel
          ? formFieldLabel &&
            formFieldLabel.trim().length > 0 && (
              <KendoLabel editorId={id}>
                <div
                  dangerouslySetInnerHTML={getHtmlFromMarkdown(formFieldLabel)}
                />
              </KendoLabel>
            )
          : formFieldLabel &&
            formFieldLabel.trim().length > 0 && (
              <Label
                editorId={id}
                formFieldLabel={formFieldLabel}
                formFieldLabelDetails={formFieldLabelDetails}
              />
            )}
        {!required && (
          <span className="tw-text-xs tw-font-normal tw-text-unum-neutral-500 tw-mb-3 tw-ml-2">
            (optional)
          </span>
        )}
      </div>
      <div className={'k-form-field-wrap tw-ml-0'}>
        {flags.card ? (
          <FieldCard>
            <FieldComponent
              id={id}
              value={value}
              touched={touched}
              onKeyDown={handleFormFieldKeyDown}
              {...others}
              onChange={onChange({
                attribute1: 'file a claim',
                attribute2: sectionname,
                attribute3: 'claim_navigation',
                attribute4: 'undefined',
                kendoComponent: 'yes',
              })}
              onBlur={onBlur}
              name={name}
              formname={formName}
              formfieldlabel={formFieldLabel}
            />
          </FieldCard>
        ) : (
          <FieldComponent
            id={id}
            value={value}
            touched={touched}
            onKeyDown={handleFormFieldKeyDown}
            {...others}
            onChange={onChange({
              attribute1: 'file a claim',
              attribute2: sectionname,
              attribute3: 'claim_interaction',
              attribute4: 'undefined',
              kendoComponent: 'yes',
            })}
            onBlur={onBlur}
            name={name}
            formname={formName}
            formfieldlabel={formFieldLabel}
          />
        )}
        {touched && validationMessage && (
          <KendoError id={`${id}_error`}>{validationMessage}</KendoError>
        )}
      </div>
    </FieldWrapper>
  );
};
